import {
  ATHLETE_PROFILE_KEY,
  type AthleteProfileInput,
  type AthleteProfileRecord,
  buildAthleteProfileRecord,
} from '~/models/athleteProfile'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

export async function getProfile(
  db: SolaDatabase,
): Promise<Result<AthleteProfileRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.profile, ATHLETE_PROFILE_KEY))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Create or replace the profile.
 *
 * Construction and validation live in the model so onboarding can build this
 * record alongside phase and goal and commit all three atomically. `createdAt`
 * is preserved across updates so an edit does not rewrite the record's history.
 */
export async function saveProfile(
  db: SolaDatabase,
  input: AthleteProfileInput,
  now: Date,
): Promise<Result<AthleteProfileRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.profile, ATHLETE_PROFILE_KEY)
    const built = buildAthleteProfileRecord(input, toIsoTimestamp(now), existing?.createdAt)
    if (!built.ok) {
      return err(
        storageError(
          'invalidRecord',
          built.error.map((entry) => entry.message).join(' '),
          built.error,
        ),
      )
    }

    await db.put(STORE.profile, built.value)
    notifyWritten(STORE.profile)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
